import { create } from "zustand";
import { createJSONStorage, persist, type StateStorage } from "zustand/middleware";

/**
 * The terminal dock's UI state (fs#2533): the CloudShell-style bottom dock
 * that holds one workspace terminal tab per project.
 *
 * A per-viewer PREFERENCE, persisted like `sidebar` — whether the dock is
 * open, how tall it is, and which project tabs are open. It is never an
 * authority over a session: a tab only names a project whose terminal the
 * dock draws, and `TerminalPanel` asks the server what that project's
 * workspace is doing.
 *
 * Starting a workspace (a billed session) takes an explicit click, and only
 * two clicks carry one (fs#2537): the rail's Terminal entry when it OPENS the
 * dock ({@link TerminalDockState.toggle}) and the Workbench's "Open in dock"
 * ({@link TerminalDockState.launch}). Each leaves a one-shot
 * {@link TerminalDockState.startRequest} that the target tab's panel consumes
 * through its own Start code path. The request is NEVER persisted, so a
 * reload — even with the dock persisted open — starts nothing, and neither
 * does a tab switch, a picker pick or a route change.
 *
 * Keyed to the viewer: `owner` is the session that wrote the tabs, and
 * {@link TerminalDockState.claim} drops every tab when another session
 * reads the entry, so a shared browser never shows one person's project
 * names to the next. An explicit sign-out clears it too (`useLogout`).
 */

/** The dock's height, as a share of the space beside the rail, when first opened. */
export const DOCK_DEFAULT_PERCENT = 40;
/** The shortest the dock may be dragged, in percent. */
export const DOCK_MIN_PERCENT = 15;
/** The tallest the dock may be dragged, in percent; Maximize goes past it to the whole column. */
export const DOCK_MAX_PERCENT = 85;

export interface TerminalDockState {
  open: boolean;
  maximized: boolean;
  /** The dock's share of the workspace column, `DOCK_MIN_PERCENT`..`DOCK_MAX_PERCENT`. */
  heightPercent: number;
  /** The open tabs, one project slug each, in strip order. */
  tabs: string[];
  /** The tab on show, or null with no tabs. */
  active: string | null;
  /** `<tenant_id>/<user_id>` of the session the tabs belong to. */
  owner: string | null;
  /**
   * A click's pending "start this project's workspace if it is not running"
   * (fs#2537), or null. `id` tells two clicks apart, so the panel acts on
   * each request once. In memory only: never persisted.
   */
  startRequest: StartRequest | null;
  /**
   * Make `owner` the viewer. A different viewer starts from the defaults:
   * closed, no tabs.
   */
  claim: (owner: string) => void;
  /**
   * The rail's Terminal entry. Opening inside a project route opens or
   * focuses that project's tab; outside one, the last tabs come back as they
   * were (or the dock offers a project picker when there are none). Opening
   * also requests a start for the tab it lands on — none when there is no
   * tab. Closing clears any request still pending.
   */
  toggle: (currentSlug: string | null) => void;
  /** Open the dock on `slug`'s tab, adding it if it is not open yet. Starts nothing. */
  openTab: (slug: string) => void;
  /** The Workbench's "Open in dock": {@link openTab} plus a start request for `slug`. */
  launch: (slug: string) => void;
  /** Drop the start request `id` once the panel has acted on it (or chosen not to). */
  clearStartRequest: (id: number) => void;
  focusTab: (slug: string) => void;
  /** Close one tab. It detaches the view only; the workspace keeps running. */
  closeTab: (slug: string) => void;
  close: () => void;
  toggleMaximized: () => void;
  /** A dragged or keyed height. Leaves a maximized dock at the new height. */
  setHeightPercent: (percent: number) => void;
  /** Back to the defaults, owner included — sign-out and tests. */
  reset: () => void;
}

/** See {@link TerminalDockState.startRequest}. */
export interface StartRequest {
  slug: string;
  id: number;
}

const DEFAULTS = {
  open: false,
  maximized: false,
  heightPercent: DOCK_DEFAULT_PERCENT,
  tabs: [] as string[],
  active: null as string | null,
  owner: null as string | null,
  startRequest: null as StartRequest | null,
};

/** Monotonic, so a request never reuses an id a panel has already handled. */
let nextRequestId = 0;

function requestStart(slug: string | null): StartRequest | null {
  if (slug === null) return null;
  nextRequestId += 1;
  return { slug, id: nextRequestId };
}

function clampPercent(value: number): number {
  if (!Number.isFinite(value)) return DOCK_DEFAULT_PERCENT;
  return Math.min(DOCK_MAX_PERCENT, Math.max(DOCK_MIN_PERCENT, value));
}

function withTab(tabs: string[], slug: string): string[] {
  return tabs.includes(slug) ? tabs : [...tabs, slug];
}

/**
 * localStorage that never throws. A browser that blocks storage (a privacy
 * mode, a sandboxed frame) throws on access, and a full one throws on write;
 * either must cost the dock its memory, never the page its rendering.
 */
export const safeLocalStorage: StateStorage = {
  getItem: (name) => {
    try {
      return window.localStorage.getItem(name);
    } catch {
      return null;
    }
  },
  setItem: (name, value) => {
    try {
      window.localStorage.setItem(name, value);
    } catch {
      // Unpersisted, not broken.
    }
  },
  removeItem: (name) => {
    try {
      window.localStorage.removeItem(name);
    } catch {
      // As above.
    }
  },
};

type Persisted = Pick<
  TerminalDockState,
  "open" | "maximized" | "heightPercent" | "tabs" | "active" | "owner"
>;

/**
 * A rehydrated entry, checked field by field: localStorage is user-editable,
 * so anything that does not parse falls back to its default.
 */
export function asPersistedDock(value: unknown): Persisted {
  const raw = (typeof value === "object" && value !== null ? value : {}) as Record<string, unknown>;
  const tabs = Array.isArray(raw.tabs)
    ? [...new Set(raw.tabs.filter((t): t is string => typeof t === "string" && t !== ""))]
    : [];
  const active = typeof raw.active === "string" && tabs.includes(raw.active) ? raw.active : null;
  return {
    open: raw.open === true,
    maximized: raw.maximized === true,
    heightPercent:
      typeof raw.heightPercent === "number"
        ? clampPercent(raw.heightPercent)
        : DOCK_DEFAULT_PERCENT,
    tabs,
    active: active ?? tabs[0] ?? null,
    owner: typeof raw.owner === "string" && raw.owner !== "" ? raw.owner : null,
  };
}

export const useTerminalDockStore = create<TerminalDockState>()(
  persist(
    (set) => ({
      ...DEFAULTS,
      claim: (owner) => set((s) => (s.owner === owner ? s : { ...DEFAULTS, owner })),
      toggle: (currentSlug) =>
        set((s) => {
          if (s.open) return { open: false, maximized: false, startRequest: null };
          if (currentSlug === null) {
            const active = s.active ?? s.tabs[0] ?? null;
            return { open: true, active, startRequest: requestStart(active) };
          }
          return {
            open: true,
            tabs: withTab(s.tabs, currentSlug),
            active: currentSlug,
            startRequest: requestStart(currentSlug),
          };
        }),
      openTab: (slug) => set((s) => ({ open: true, tabs: withTab(s.tabs, slug), active: slug })),
      launch: (slug) =>
        set((s) => ({
          open: true,
          tabs: withTab(s.tabs, slug),
          active: slug,
          startRequest: requestStart(slug),
        })),
      clearStartRequest: (id) =>
        set((s) => (s.startRequest?.id === id ? { startRequest: null } : s)),
      focusTab: (slug) => set((s) => (s.tabs.includes(slug) ? { active: slug } : s)),
      closeTab: (slug) =>
        set((s) => {
          const index = s.tabs.indexOf(slug);
          if (index === -1) return s;
          const tabs = s.tabs.filter((t) => t !== slug);
          const active = s.active === slug ? (tabs[index] ?? tabs[index - 1] ?? null) : s.active;
          const startRequest = s.startRequest?.slug === slug ? null : s.startRequest;
          return { tabs, active, startRequest };
        }),
      close: () => set({ open: false, maximized: false, startRequest: null }),
      toggleMaximized: () => set((s) => ({ maximized: !s.maximized })),
      setHeightPercent: (percent) =>
        set({ heightPercent: clampPercent(percent), maximized: false }),
      reset: () => set({ ...DEFAULTS }),
    }),
    {
      name: "flowstack-terminal-dock",
      version: 1,
      storage: createJSONStorage(() => safeLocalStorage),
      partialize: (s): Persisted => ({
        open: s.open,
        maximized: s.maximized,
        heightPercent: s.heightPercent,
        tabs: s.tabs,
        active: s.active,
        owner: s.owner,
      }),
      merge: (persisted, current) => ({ ...current, ...asPersistedDock(persisted) }),
    }
  )
);

/** The owner key {@link TerminalDockState.claim} takes for a session. */
export function dockOwner(session: { user_id: string; tenant_id: string }): string {
  return `${session.tenant_id}/${session.user_id}`;
}
